/**
 * Reference production rates — the estimator's starting point, never the
 * engine's input.
 *
 * A commercial bid is not built from "how long does this room take". It is
 * built from a measured quantity and a rate: 12,000 square feet of open
 * office, vacuumed at 3,000 square feet an hour, is four hours. The rates
 * below are mid-range figures in the tradition of the **ISSA 612 Cleaning
 * Times** book, which is the table commercial cleaners will expect to
 * recognize and the one they argue with each other about. They are for ONE
 * cleaner with ordinary equipment, which is how the book states them and how
 * janitorial crews actually work.
 *
 * Three things this file deliberately is not:
 *
 *   * It is not a reproduction of ISSA 612. The book is copyrighted and sold;
 *     these are independent mid-range figures for the same operations, in the
 *     same units, so a cleaner who owns the book can reconcile them and a
 *     cleaner who does not still starts somewhere defensible.
 *   * It is not pricing input. Nothing in `calculateQuote` reads this file.
 *     The rate that prices a bid is the one stored on the task line, which
 *     starts here, is offered to the estimator, and is theirs to overrule —
 *     the same contract the seeded task library has always had.
 *   * It is not exhaustive. A task with no entry here simply has no
 *     suggestion, and the estimator types a rate or prices the line by time.
 *
 * Rates are integers in **units per hour**: square feet per hour for 'area'
 * tasks, fixtures per hour for 'fixture' tasks.
 */

import type { FloorType, MeasurementBasis } from './index.ts';

export interface ReferenceRate {
  /** How this task is measured when a rate is applied. */
  readonly basis: Exclude<MeasurementBasis, 'time'>;
  /**
   * The rate to offer when the area's floor type is unknown, or is a type
   * with no entry of its own.
   */
  readonly defaultRatePerHour: number;
  /**
   * Rates for the floor types this task's speed genuinely depends on.
   *
   * Sparse on purpose: emptying bins takes the same time over carpet as over
   * concrete, and inventing a per-floor-type number for it would dress a
   * guess up as a measurement.
   */
  readonly byFloorType?: Partial<Record<FloorType, number>>;
}

/**
 * Keyed by the seed library's own service keys (`seed-templates-commercial.ts`),
 * because that is what a company's task rows are seeded from and therefore the
 * only stable identity a task has before an owner renames it.
 */
export const REFERENCE_PRODUCTION_RATES: Readonly<Record<string, ReferenceRate>> = {
  // --- Nightly janitorial, measured by floor area --------------------------
  c_empty_trash: {
    basis: 'area',
    // Bins per thousand square feet is roughly constant across office
    // layouts, so the whole operation scales with area well enough to bid on.
    defaultRatePerHour: 12_000,
  },
  c_dust_surfaces: {
    basis: 'area',
    defaultRatePerHour: 7_000,
    byFloorType: {
      // Not the floor — the furniture density that comes with it. Open plan
      // over carpet is desks every few feet; a warehouse over concrete is
      // rack uprights and not much else.
      carpet: 6_000,
      concrete: 12_000,
    },
  },
  c_vacuum_carpet: {
    basis: 'area',
    // An upright over open carpet with the desks cleared. Detail vacuuming
    // edges and under furniture is roughly half this, which is why the
    // estimator gets to lower it.
    defaultRatePerHour: 3_000,
    byFloorType: {
      carpet: 3_000,
      mixed: 2_500,
    },
  },
  c_mop_hard_floors: {
    basis: 'area',
    defaultRatePerHour: 3_500,
    byFloorType: {
      resilient: 4_000,
      hard: 3_000,
      // Sealed concrete takes a bigger head and fewer obstacles.
      concrete: 6_000,
      mixed: 3_000,
    },
  },
  c_glass_doors: {
    basis: 'area',
    // Priced against the lobby's floor area rather than glass area, which is
    // what an estimator can actually measure on a walkthrough.
    defaultRatePerHour: 9_000,
  },
  c_spot_clean_touch_points: {
    basis: 'area',
    defaultRatePerHour: 15_000,
  },
  c_high_dust: {
    basis: 'area',
    // Ladder work: vents, ledges, light fittings, the tops of partitions.
    defaultRatePerHour: 4_000,
  },

  // --- Restrooms, measured per fixture ------------------------------------
  c_restroom_fixtures: {
    basis: 'fixture',
    // Clean and disinfect, one fixture at a time, restocking as you go.
    defaultRatePerHour: 30,
  },
  c_clean_toilets: {
    basis: 'fixture',
    defaultRatePerHour: 24,
  },
  c_clean_urinals: {
    basis: 'fixture',
    defaultRatePerHour: 30,
  },
  c_clean_sinks: {
    basis: 'fixture',
    defaultRatePerHour: 40,
  },

  // --- Periodic floor work -------------------------------------------------
  c_buff_floors: {
    basis: 'area',
    defaultRatePerHour: 12_000,
    byFloorType: {
      resilient: 14_000,
      hard: 10_000,
      concrete: 16_000,
    },
  },
  c_carpet_extraction: {
    basis: 'area',
    // Hot-water extraction with a walk-behind, moving what furniture moves.
    defaultRatePerHour: 1_200,
  },
  c_strip_and_wax: {
    basis: 'area',
    // Strip, neutralise, and lay finish. The slowest thing in the book, and
    // the line most often underbid.
    defaultRatePerHour: 400,
  },
  c_auto_scrub: {
    basis: 'area',
    defaultRatePerHour: 20_000,
    byFloorType: {
      // A rider in a clear aisle against a walk-behind between racking.
      concrete: 30_000,
      resilient: 18_000,
      hard: 16_000,
    },
  },
  c_sweep_warehouse: {
    basis: 'area',
    defaultRatePerHour: 25_000,
  },
  c_sanitize_surfaces: {
    basis: 'area',
    defaultRatePerHour: 8_000,
  },
  c_dust_merchandise: {
    basis: 'area',
    defaultRatePerHour: 5_000,
  },
  c_clean_lockers: {
    basis: 'fixture',
    defaultRatePerHour: 20,
  },
  c_clean_machines: {
    basis: 'fixture',
    defaultRatePerHour: 12,
  },
};

/**
 * The rate to offer for a task on an area with this floor underfoot, or
 * `null` when there is nothing to suggest.
 *
 * `null` rather than a fallback number, because a suggestion nobody stands
 * behind is worse than no suggestion: an estimator who sees a figure assumes
 * it came from somewhere.
 */
export function referenceRateFor(serviceKey: string | null | undefined, floorType?: FloorType | null): number | null {
  if (!serviceKey) return null;
  const entry = REFERENCE_PRODUCTION_RATES[serviceKey];
  if (!entry) return null;
  const byFloor = floorType ? entry.byFloorType?.[floorType] : undefined;
  return byFloor ?? entry.defaultRatePerHour;
}
